import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function OpeningHourManagement() {
  const authCtx = useContext(AuthContext);

  if (!authCtx.checkRight('openingHour:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const openingHourSchema = {
    name: {
      label: 'Name',
      api: { itemField: 'name' },
      placeholder: 'Name eingeben',
      type: 'text',
      required: true,
      minWidth: '200px',
    },
    description: {
      label: 'Beschreibung',
      api: { itemField: 'description' },
      placeholder: 'Beschreibung eingeben',
      type: 'text',
      required: false,
      minWidth: '240px',
    },
    priority: {
      label: 'Priorität',
      api: { itemField: 'priority' },
      placeholder: 'Priorität (Zahl)',
      type: 'number',
      required: true,
      minWidth: '120px',
    },
    validFrom: {
      label: 'Gültig von',
      api: { itemField: 'validFrom' },
      placeholder: 'Datum auswählen',
      type: 'date',
      required: true,
      minWidth: '160px',
    },
    validTo: {
      label: 'Gültig bis',
      api: { itemField: 'validTo' },
      placeholder: 'Datum auswählen',
      type: 'date',
      required: true,
      minWidth: '160px',
    },
    specialTypes: {
      label: 'Spezialtypen',
      type: 'multi-select',
      placeholder: 'Spezialtypen wählen',
      required: false,
      minWidth: '200px',
      options: [
        { value: 'holiday', label: 'Feiertag' },
        { value: 'vacation', label: 'Ferien' },
        { value: 'summerVacation', label: 'Sommerferien' },
        { value: 'bridgeDay', label: 'Brückentag' },
      ],
    },
    hours: {
      label: 'Zeiten',
      type: 'nested',
      addButtonText: 'Neue Zeit hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'hours',
        nestedEndpoint: 'hour',
      },
      schema: {
        dayCode: {
          label: 'Wochentage (0=So, 1=Mo, ...)',
          type: 'array',
          placeholder: 'z. B. 1,2,3',
          transformValue: value =>
            value
              .toString()
              .split(',')
              .map(v => parseInt(String(v).trim(), 10))
              .filter(v => !Number.isNaN(v)),
          listDisplayValue: value => {
            if (!value || !Array.isArray(value) || value.length === 0) return '—';
            return value.join(', ');
          },
          required: true,
        },
        isClosed: {
          label: 'Geschlossen',
          type: 'boolean',
          required: false,
        },
        start: {
          label: 'Start (HH:MM)',
          type: 'time',
          required: true,
        },
        end: {
          label: 'Ende (HH:MM)',
          type: 'time',
          required: true,
        },
      },
    },
    assignedEntertainments: {
      label: 'Zugeordnete Entertainments',
      type: 'multi-select',
      api: {
        fieldName: 'entertainment',
        itemField: 'assignedEntertainments',
        idName: 'entertainmentId',
        resource: 'entertainment',
        labelField: 'longName',
        valueField: '_id',
        skipSpecial: true,
        objectify: { idProp: 'entertainmentId', labelProp: 'name' },
      },
      placeholder: 'Entertainments auswählen',
      required: false,
      transformValue: (selectedValues, options) => {
        const values = Array.isArray(selectedValues) ? selectedValues : selectedValues ? [selectedValues] : [];
        return values.map(value => {
          if (value && typeof value === 'object') {
            return {
              entertainmentId:
                value.entertainmentId || value._id || value.id || value.value || null,
              name: value.name || value.label || value.longName || '',
            };
          }

          const found = (options || []).find(option => String(option?.value) === String(value));
          return {
            entertainmentId: found ? found.value : value,
            name: found?.label || '',
          };
        });
      },
      listDisplayValue: values => {
        if (!Array.isArray(values) || values.length === 0) return '—';
        return values
          .map(v => v?.name || '')
          .filter(Boolean)
          .join(', ');
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={openingHourSchema}
      title='Neue Öffnungszeit anlegen'
      resourceType='openingHour'
      headerSearchPlaceholder='Öffnungszeiten durchsuchen'
      parentIsEditable={authCtx.checkRight('openingHour:update')}
      options={{ titleKey: 'name' }}
    />
    </Box>
  );
}

export default OpeningHourManagement;
